import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { toast } from "react-toastify";
import { IconsArray } from "../../../../../constants/Icons";

const USER_ROLES = [
  "agency_admins",
  "agency_users",
  "account_admins",
  "account_users",
];

const AddNewMenuModal = ({ data, handleRefresh }) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
        ? AppService.sidenav_customizer_custom_level
        : AppService.sidenav_customizer_saas_level;

  const [menuItems, setMenuItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState("");
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [label, setLabel] = useState("");
  const [link, setLink] = useState("");
  const [selector, setSelector] = useState("");
  const [menuType, setMenuType] = useState("parent");
  const [parentMenu, setParentMenu] = useState("");
  const [permissions, setPermissions] = useState(() =>
    USER_ROLES.reduce((acc, role) => ({ ...acc, [role]: false }), {}),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (data?.menus) {
      setMenuItems(data.menus);
    }
  }, [data]);

  const resetPermissions = () =>
    setPermissions(
      USER_ROLES.reduce((acc, role) => ({ ...acc, [role]: false }), {}),
    );

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetPermissions();
    setLink("");
    setLabel("");
    setSelectedIcon(null);
    setMenuType("");
    setParentMenu("");
    setSelector("");
    setIconSearch("");
    setShowIconSelector(false);
  };

  const handlePermissionsChange = (e) => {
    const { name, checked } = e.target;
    setPermissions((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async () => {
    if (!menuType) {
      toast.warning("Please select menu type (Parent or Child).");
      return;
    }

    if (menuType === "child" && !parentMenu) {
      toast.warning("Please select a parent menu for child.");
      return;
    }
    setSubmitting(true);
    let customMenuLinkId = null;
    if (menuType == "parent") {
      const customMenuLinkResponse =
        await AppService.webhook.create_ghl_custom_menu_link({
          payload: {
            companyId,
            showToAllLocations: type != "group" ? true : false,
            customMenuLinkTitle: label,
            locations:
              type != "group"
                ? []
                : data?.locations?.map((loc) => loc.id) || [],
            url: link,
            icon: {
              name: selectedIcon ? selectedIcon.name : "yin-yang",
              fontFamily: selectedIcon ? selectedIcon.fontFamily : "fab",
            },
          },
        });
      if (customMenuLinkResponse?.response?.data?.id) {
        customMenuLinkId = customMenuLinkResponse.response.data.id;
      }
    }
    const iconHtml = selectedIcon ? `&#x${selectedIcon.unicode};` : "";
    const newMenu = {
      label: label,
      newLabel: label.trim(),
      value: menuType == "parent" ? customMenuLinkId : selector,
      link: link,
      selector: menuType == "parent" ? customMenuLinkId : selector,
      level: menuType == "parent" ? "sidebar" : "nested",
      icon: iconHtml,
      permissions,
      isCustom: true,
    };

    let newMenus = [...menuItems];

    if (menuType === "parent") {
      newMenus.push({ ...newMenu, children: [] });
    } else {
      newMenus = newMenus.map((menu) =>
        menu.selector === parentMenu
          ? { ...menu, children: [...(menu.children || []), newMenu] }
          : menu,
      );
    }

    const payload = {
      companyId,
      menus: newMenus,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        setMenuItems(newMenus);
        handleClose();
        handleRefresh();
        toast.success("Menu item added successfully.");
      } else {
        toast.error("Failed to add menu.");
      }
    } catch (error) {
      console.error("Error adding menu:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredIcons = IconsArray.filter((icon) =>
    icon.name.toLowerCase().includes(iconSearch.toLowerCase()),
  );

  return (
    <>
      <button
        type="button"
        className="mm-trigger mm-trigger--primary d-inline-flex align-items-center"
        title="Add Menu"
        onClick={handleOpen}
      >
        <span className="mm-trigger-icon d-inline-flex align-items-center justify-content-center" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        <span className="mm-trigger-label">Add new menu</span>
      </button>

      {open && (
        <div className="mm-overlay" onMouseDown={handleClose}>
          <div
            className="mm-dialog"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="mm-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mm-title mb-0">Add menu item</h5>
                <p className="mm-subtitle mb-0">Create a parent menu or add a child under an existing one.</p>
              </div>
              <button type="button" className="mm-close" onClick={handleClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </header>

            <div className="mm-body">
              <div className="mm-field">
                <label className="mm-label">Menu type</label>
                <select
                  className="mm-input"
                  value={menuType}
                  onChange={(e) => setMenuType(e.target.value)}
                >
                  <option value="">Select type</option>
                  <option value="parent">Parent</option>
                  <option value="child">Child</option>
                </select>
              </div>

              {menuType === "child" && (
                <div className="mm-field">
                  <label className="mm-label">Parent menu</label>
                  <select
                    className="mm-input"
                    value={parentMenu}
                    onChange={(e) => setParentMenu(e.target.value)}
                  >
                    <option value="">Select parent</option>
                    {menuItems.map((menu, i) => (
                      <option key={i} value={menu.selector}>
                        {menu.newLabel || menu.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {menuType && (
                <>
                  <div className="mm-field">
                    <label className="mm-label">Menu label</label>
                    <input
                      className="mm-input"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g. Reports"
                    />
                  </div>

                  <div className="mm-field">
                    <label className="mm-label">Menu link</label>
                    <input
                      className="mm-input"
                      value={link}
                      onChange={(e) => setLink(e.target.value)}
                      placeholder="https://"
                    />
                  </div>

                  <div className="mm-field">
                    <label className="mm-label">
                      CSS ID
                      <span className="mm-label-hint">
                        Ensure the ID is unique and does not conflict with other element IDs.
                      </span>
                    </label>
                    <input
                      type="text"
                      name="id"
                      className="mm-input"
                      value={selector}
                      onChange={(e) => setSelector(e.target.value)}
                      placeholder="my-custom-menu"
                    />
                  </div>

                  <div className="mm-field">
                    <label className="mm-label">Icon</label>
                    <div className="mm-icon-input-wrap">
                      {selectedIcon && (
                        <span className="mm-icon-preview">
                          <i className={`fas fa-${selectedIcon.name}`}></i>
                        </span>
                      )}
                      <input
                        className="mm-input mm-input--with-prefix"
                        placeholder="Search icon"
                        value={showIconSelector ? iconSearch : selectedIcon?.name || ""}
                        onClick={() => setShowIconSelector((prev) => !prev)}
                        onChange={(e) => setIconSearch(e.target.value)}
                        readOnly={!showIconSelector}
                        style={selectedIcon ? { paddingLeft: 44 } : {}}
                      />
                      <button
                        type="button"
                        className="mm-input-action"
                        onClick={() => setShowIconSelector((prev) => !prev)}
                        aria-label="Toggle icon picker"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    </div>

                    {showIconSelector && (
                      <div className="mm-icon-grid">
                        {filteredIcons.length === 0 ? (
                          <p className="mm-icon-empty">No icons found.</p>
                        ) : (
                          <div className="mm-icon-grid-inner">
                            {filteredIcons.map((icon, i) => (
                              <button
                                key={i}
                                type="button"
                                className={`mm-icon-cell ${selectedIcon?.name === icon.name ? "is-selected" : ""}`}
                                onClick={() => {
                                  setSelectedIcon(icon);
                                  setShowIconSelector(false);
                                }}
                                title={icon.name}
                              >
                                <i className={`fas fa-${icon.name}`}></i>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mm-field">
                    <label className="mm-label">Visible to</label>
                    <div className="mm-checkboxes">
                      {USER_ROLES.map((role) => (
                        <label className="mm-checkbox" key={role}>
                          <input
                            type="checkbox"
                            name={role}
                            checked={permissions[role]}
                            onChange={handlePermissionsChange}
                          />
                          <span className="mm-checkbox-box" aria-hidden="true">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                          <span className="text-capitalize">{role.replace("_", " ")}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <footer className="mm-footer d-flex justify-content-end gap-2">
              <button type="button" className="mm-btn mm-btn--ghost" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
              <button type="button" className="mm-btn mm-btn--primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Adding
                  </>
                ) : (
                  "Add menu"
                )}
              </button>
            </footer>
          </div>
        </div>
      )}

      <MMStyles />
    </>
  );
};

const MMStyles = () => (
  <style>{`
    .mm-trigger {
      position: relative;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 16px 8px 8px;
      border-radius: 9px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
      font-family: 'Inter', sans-serif;
      gap: 8px;
    }
    .mm-trigger:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 6px 14px rgba(79, 70, 229, 0.28);
    }
    .mm-trigger--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.25),
        0 6px 14px rgba(79, 70, 229, 0.22);
    }
    .mm-trigger--primary:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.3),
        0 10px 22px rgba(79, 70, 229, 0.32);
    }
    .mm-trigger--primary:active {
      transform: translateY(0);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(67, 56, 202, 0.3);
    }
    .mm-trigger-icon {
      width: 22px;
      height: 22px;
      border-radius: 6px;
      background: rgba(255, 255, 255, 0.18);
      color: #fff;
      flex-shrink: 0;
      transition: background 0.2s ease, transform 0.2s ease;
    }
    .mm-trigger--primary:hover .mm-trigger-icon {
      background: rgba(255, 255, 255, 0.26);
      transform: rotate(90deg);
    }
    .mm-trigger-label {
      padding-right: 2px;
    }

    .mm-overlay {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.45);
      backdrop-filter: blur(2px);
      z-index: 1050;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      animation: mmFade 0.18s ease;
      font-family: 'Inter', sans-serif;
    }
    @keyframes mmFade {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .mm-dialog {
      background: #fff;
      border-radius: 14px;
      border: 1px solid #ececec;
      width: 100%;
      max-width: 520px;
      max-height: calc(100vh - 48px);
      display: flex;
      flex-direction: column;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
      animation: mmPop 0.18s ease;
      overflow: hidden;
    }
    @keyframes mmPop {
      from { opacity: 0; transform: scale(0.97) translateY(6px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }
    .mm-header {
      padding: 18px 22px 14px;
    }
    .mm-title {
      font-size: 1.0625rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .mm-subtitle {
      font-size: 0.8125rem;
      color: #6b7280;
      margin-top: 2px !important;
    }
    .mm-close {
      width: 30px;
      height: 30px;
      border-radius: 8px;
      background: transparent;
      border: 0;
      color: #6b7280;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .mm-close:hover {
      background: #f3f4f6;
      color: #1f2937;
    }
    .mm-body {
      padding: 6px 22px 16px;
      overflow-y: auto;
    }
    .mm-footer {
      padding: 14px 22px 18px;
      border-top: 1px solid #f1f1f1;
      background: #fff;
    }

    .mm-field { margin-bottom: 16px; }
    .mm-field:last-child { margin-bottom: 4px; }
    .mm-label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #1f2937;
      margin-bottom: 6px;
    }
    .mm-label-hint {
      display: block;
      font-size: 0.6875rem;
      font-weight: 400;
      color: #9ca3af;
      margin-top: 2px;
    }
    .mm-input {
      width: 100%;
      height: 38px;
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      padding: 0 12px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    select.mm-input {
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 12px center;
      padding-right: 32px;
    }
    .mm-input::placeholder { color: #9ca3af; }
    .mm-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }

    .mm-icon-input-wrap {
      position: relative;
    }
    .mm-icon-preview {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 26px;
      height: 26px;
      border-radius: 6px;
      background: #f3f4f6;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: #1f2937;
      font-size: 0.8125rem;
      pointer-events: none;
    }
    .mm-input-action {
      position: absolute;
      right: 6px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .mm-input-action:hover {
      background: #f3f4f6;
      color: #1f2937;
    }

    .mm-icon-grid {
      margin-top: 8px;
      border: 1px solid #ececec;
      border-radius: 10px;
      background: #fafafa;
      padding: 10px;
      max-height: 180px;
      overflow-y: auto;
    }
    .mm-icon-grid-inner {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
      gap: 6px;
    }
    .mm-icon-cell {
      width: 100%;
      aspect-ratio: 1 / 1;
      border: 1px solid #ececec;
      border-radius: 8px;
      background: #fff;
      color: #1f2937;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
      font-size: 0.8125rem;
    }
    .mm-icon-cell:hover {
      border-color: #c7d2fe;
      background: #f5f3ff;
    }
    .mm-icon-cell.is-selected {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      border-color: #4f46e5;
      color: #fff;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 6px rgba(79, 70, 229, 0.28);
    }
    .mm-icon-empty {
      margin: 0;
      font-size: 0.8125rem;
      color: #9ca3af;
      text-align: center;
      padding: 8px 0;
    }

    .mm-checkboxes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 12px;
    }
    .mm-checkbox {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-size: 0.8125rem;
      color: #1f2937;
      user-select: none;
    }
    .mm-checkbox input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }
    .mm-checkbox-box {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      border: 1px solid #d1d5db;
      background: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: transparent;
      transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
      flex-shrink: 0;
    }
    .mm-checkbox input:checked + .mm-checkbox-box {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      border-color: #4f46e5;
      color: #fff;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22);
    }
    .mm-checkbox input:focus-visible + .mm-checkbox-box {
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
    }

    .mm-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 16px;
      border-radius: 9px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
      font-family: inherit;
    }
    .mm-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
    .mm-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2), 0 4px 10px rgba(79, 70, 229, 0.18);
    }

    .mm-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .mm-btn--ghost:hover:not(:disabled) {
      background: #fafafa;
      border-color: #d1d5db;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
    }
    .mm-btn--ghost:active:not(:disabled) {
      transform: translateY(0);
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.05);
    }

    .mm-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .mm-btn--primary:hover:not(:disabled) {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 8px 18px rgba(79, 70, 229, 0.3);
    }
    .mm-btn--primary:active:not(:disabled) {
      transform: translateY(0);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(67, 56, 202, 0.25);
    }

    .mm-btn--danger {
      background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(185, 28, 28, 0.22),
        0 4px 10px rgba(220, 38, 38, 0.2);
    }
    .mm-btn--danger:hover:not(:disabled) {
      background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%);
      transform: translateY(-1px);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(153, 27, 27, 0.28),
        0 8px 18px rgba(220, 38, 38, 0.3);
    }
    .mm-btn--danger:active:not(:disabled) {
      transform: translateY(0);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(153, 27, 27, 0.25);
    }
  `}</style>
);

export { MMStyles };
export default AddNewMenuModal;
